import React, { useEffect, useState, useCallback } from 'react';
import {
    TextField, Paper, Button, Typography, CircularProgress, Grid, IconButton,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Autocomplete, Chip, Box, Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, ToggleButton, ToggleButtonGroup, InputAdornment, Tooltip, Divider,
    Tabs, Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import AddIcon from '@mui/icons-material/Add';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import apiService from '../../services/actions';
import { toast } from 'react-toastify';
import axios from 'axios';

const T = {
    primary: '#1976d2',
    primaryLight: '#e3f2fd',
    primaryMid: '#bbdefb',
    primaryDark: '#1565c0',
    secondary: '#9c27b0',
    success: '#2e7d32',
    successBg: '#e8f5e9',
    successMid: '#a5d6a7',
    error: '#c62828',
    errorBg: '#ffebee',
    errorMid: '#ef9a9a',
    border: '#e0e0e0',
    rowHover: '#f0f7ff',
    rowSelected: '#e3f2fd',
    rowAlt: '#fafafa',
    textPrimary: '#212121',
    textSecondary: '#616161',
    sectionBg: '#f8fafc',
    disabled: '#bdbdbd',
};

const EMPTY_MANUAL_FORM = { fullName: '', phoneNumber: '', email: '' };
const EMPTY_MANUAL_ECLINIC_FORM = { eclinicCode: '', eclinicName: '', city: '', state: '', country: '', zipcode: '' };

const CLINIC_TABS = [
    { value: 'ECLINIC', label: 'eClinic' },
    { value: 'COLLECTION_CENTER', label: 'Collection Center' },
];

const normalisePhone = (phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits || digits.length < 10) return digits;
    if (digits.startsWith('91') && digits.length === 12) return digits;
    return `91${digits}`;
};

function OwnerAvatar({ name }) {
    const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
        <Box sx={{
            width: 26, height: 26, borderRadius: '50%',
            background: `linear-gradient(135deg, ${T.primary} 0%, ${T.secondary} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '10px', fontWeight: 700, flexShrink: 0,
        }}>
            {initials}
        </Box>
    );
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        height: 34, borderRadius: '7px', fontSize: '0.8125rem', bgcolor: '#fff',
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
    },
    '& .MuiInputLabel-root': { fontSize: '0.8125rem', top: '-2px', '&.MuiInputLabel-shrink': { top: 0 } },
    '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
};

function MobilabClinicList() {
    const loginObjStr = localStorage.getItem('loginObj');
    const loginObj = JSON.parse(loginObjStr);
    const userCode = loginObj?.userSession?.userCode || null;
    const authToken = loginObj?.userSession?.token || loginObj?.token || null;

    const [activeTab, setActiveTab] = useState('ECLINIC');
    const isEclinic = activeTab === 'ECLINIC';
    const typeLabel = isEclinic ? 'eClinic' : 'Collection Center';

    const getOptionCode = (o) => (isEclinic ? o?.eclinicCode : o?.ccCode) || '';
    const getOptionLabel = (o) => (isEclinic ? o?.eclinicName : o?.ccName) || '';

    const [loading, setLoading] = useState(false);
    const [tableList, setTableList] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const itemsPerPage = 10;

    const [filterEclinicCode, setFilterEclinicCode] = useState('');
    const [filterEclinicOptions, setFilterEclinicOptions] = useState([]);

    const [filterUserCode, setFilterUserCode] = useState('');
    const [filterUserOptions, setFilterUserOptions] = useState([]);
    const [filterUserLoading, setFilterUserLoading] = useState(false);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addMode, setAddMode] = useState('search');
    const [addEclinicOptions, setAddEclinicOptions] = useState([]);
    const [selectedAddEclinic, setSelectedAddEclinic] = useState(null);
    const [manualEclinicForm, setManualEclinicForm] = useState(EMPTY_MANUAL_ECLINIC_FORM);
    const [addSaving, setAddSaving] = useState(false);

    const [manageOpen, setManageOpen] = useState(false);
    const [activeClinic, setActiveClinic] = useState(null);
    const [clinicUsersLoading, setClinicUsersLoading] = useState(false);
    const [clinicUsers, setClinicUsers] = useState([]);

    const [userMode, setUserMode] = useState('existing');
    const [existingOwnerOptions, setExistingOwnerOptions] = useState([]);
    const [selectedExistingOwner, setSelectedExistingOwner] = useState(null);
    const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
    const [editingUser, setEditingUser] = useState(null);
    const [userSaving, setUserSaving] = useState(false);

    const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [deleteClinicDialogOpen, setDeleteClinicDialogOpen] = useState(false);
    const [clinicToDelete, setClinicToDelete] = useState(null);
    const [clinicDeleting, setClinicDeleting] = useState(false);

    // --- Activate (inactive -> active) only ---
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [clinicToActivate, setClinicToActivate] = useState(null);
    const [activating, setActivating] = useState(false);

    const getEclinicsData = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 3) {
            setFilterEclinicOptions([]);
            return;
        }
        try {
            if (isEclinic) {
                const response = await axios.get(
                    `${process.env.REACT_APP_COMMON_URL}EclinicAddress/list?eclinicName=${searchTerm}`,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                setFilterEclinicOptions(response?.data?.data || []);
            } else {
                const response = await apiService.getCollectionCenterDropdown(searchTerm);
                setFilterEclinicOptions(response?.data?.data || []);
            }
        } catch (error) {
            setFilterEclinicOptions([]);
        }
    }, [authToken, isEclinic]);

    const getUsersData = useCallback(async (eclinicCode = '', tabType = activeTab) => {
        const eclinic = tabType === 'ECLINIC';
        setFilterUserLoading(true);
        try {
            const r = eclinic
                ? await apiService.getMobilabRegisterUsers({ eclinicCode })
                : await apiService.getCollectionUser(eclinicCode);
            setFilterUserOptions(r?.data?.data || []);
        } catch {
            setFilterUserOptions([]);
        }
        setFilterUserLoading(false);
    }, [activeTab]);

    const getTableList = async (pageNo = 0, eclinicCode = filterEclinicCode, userCodeFilter = filterUserCode, clinicType = activeTab) => {
        setLoading(true);
        try {
            const params = { page: pageNo, size: itemsPerPage, clinicType };
            if (eclinicCode) params.eclinicCode = eclinicCode;
            if (userCodeFilter) params.userCode = userCodeFilter;
            const res = await apiService.getMobilabClinicList(params);
            const data = res?.data?.data || {};
            setTableList(data.content || []);
            setCurrentPage(data.page ?? pageNo);
            setTotalPages(data.totalPages ?? 1);
            setTotalElements(data.totalElements ?? 0);
        } catch {
            setTableList([]);
            setTotalPages(1);
            setTotalElements(0);
        }
        setLoading(false);
    };

    const handleTabChange = (_, newTab) => {
        if (!newTab || newTab === activeTab) return;
        setActiveTab(newTab);
        setSelectedIndex(null);
        setFilterEclinicCode('');
        setFilterEclinicOptions([]);
        setFilterUserCode('');
        setFilterUserOptions([]);
        getTableList(0, '', '', newTab);
        getUsersData('', newTab);
        fetchAddEclinicOptions('', newTab);
    };

    const handleFilterEclinicChange = (v) => {
        const c = getOptionCode(v);
        setFilterEclinicCode(c);
        setFilterUserCode('');
        setFilterUserOptions([]);
        getTableList(0, c, '');
    };

    const handleFilterUserChange = (v) => {
        const u = v?.userCode || '';
        setFilterUserCode(u);
        getTableList(0, filterEclinicCode, u);
    };

    const handleFilterUserOpen = () => {
        getUsersData(filterEclinicCode);
    };

    const handleFilterSearch = () => getTableList(0, filterEclinicCode, filterUserCode);

    const handleFilterReset = () => {
        setFilterEclinicCode('');
        setFilterEclinicOptions([]);
        setFilterUserCode('');
        setFilterUserOptions([]);
        getTableList(0, '', '');
    };

    useEffect(() => {
        getUsersData(filterEclinicCode);

        getTableList(0, '', '', activeTab);
    }, []);

    const fetchAddEclinicOptions = async (q = '', tabType = activeTab) => {
        const eclinic = tabType === 'ECLINIC';
        try {
            const r = eclinic
                ? await apiService.getEclinicDropdown(q)
                : await apiService.getCollectionCenterDropdown(q);
            setAddEclinicOptions(r?.data?.data || []);
        } catch {
            setAddEclinicOptions([]);
        }
    };

    const openAddDialog = () => {
        setSelectedAddEclinic(null);
        setAddEclinicOptions([]);
        setManualEclinicForm(EMPTY_MANUAL_ECLINIC_FORM);
        setAddMode('search');
        setAddDialogOpen(true);
        fetchAddEclinicOptions('');
    };

    const closeAddDialog = () => {
        if (addSaving) return;
        setAddDialogOpen(false);
        setSelectedAddEclinic(null);
        setAddEclinicOptions([]);
        setManualEclinicForm(EMPTY_MANUAL_ECLINIC_FORM);
        setAddMode('search');
    };

    const handleAddModeChange = (_, mode) => {
        if (!mode) return;
        setAddMode(mode);
        setSelectedAddEclinic(null);
        setManualEclinicForm(EMPTY_MANUAL_ECLINIC_FORM);
    };

    const handleAddClinic = async () => {
        if (isEclinic && addMode === 'manual') {
            if (!manualEclinicForm.eclinicCode || !manualEclinicForm.eclinicName) {
                toast.error(`Please enter ${typeLabel.toLowerCase()} code and name.`);
                return;
            }
            setAddSaving(true);
            try {
                const payload = {
                    eclinicCode: manualEclinicForm.eclinicCode,
                    eclinicName: manualEclinicForm.eclinicName,
                    city: manualEclinicForm.city || '',
                    state: manualEclinicForm.state || '',
                    country: manualEclinicForm.country || '',
                    zipcode: manualEclinicForm.zipcode || '',
                    clinicType: activeTab,
                    createdBy: userCode,
                };
                const res = await apiService.saveMobilabClinic(payload);
                if (res?.data?.status === true || res?.data?.responseCode === '200' || res?.status === 200) {
                    toast.success(`${typeLabel} added successfully.`);
                    closeAddDialog();
                    getTableList(0);
                } else {
                    toast.error(res?.data?.reponseMessage || 'Save failed.');
                }
            } catch {
                toast.error('Something went wrong.');
            }
            setAddSaving(false);
            return;
        }

        if (!selectedAddEclinic) {
            toast.error(`Please select a ${typeLabel.toLowerCase()}.`);
            return;
        }
        setAddSaving(true);
        try {
            const payload = {
                eclinicCode: getOptionCode(selectedAddEclinic),
                eclinicName: getOptionLabel(selectedAddEclinic),
                city: selectedAddEclinic.city || '',
                state: selectedAddEclinic.state || '',
                country: selectedAddEclinic.country || '',
                zipcode: selectedAddEclinic.pincode || selectedAddEclinic.zipcode || '',
                clinicType: activeTab,
                createdBy: userCode,
            };
            const res = await apiService.saveMobilabClinic(payload);
            if (res?.data?.status === true || res?.data?.responseCode === '200' || res?.status === 200) {
                toast.success(`${typeLabel} added successfully.`);
                closeAddDialog();
                getTableList(0);
            } else {
                toast.error(res?.data?.reponseMessage || 'Save failed.');
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setAddSaving(false);
    };

    const fetchClinicUsers = async (eclinicCode) => {
        setClinicUsersLoading(true);
        try {
            const res = await apiService.getMobilabUsers({ eclinicCode, page: 0, size: 50 });
            const data = res?.data?.data || {};
            setClinicUsers(data.content || []);
        } catch {
            setClinicUsers([]);
        }
        setClinicUsersLoading(false);
    };

    const fetchExistingOwners = async (eclinicCode) => {
        try {
            let payload = { hospitalId: eclinicCode }
            const r = isEclinic
                ? await apiService.getMobilabHospitalUsers(payload)
                : await apiService.getCollectionUser(eclinicCode);
            setExistingOwnerOptions(r?.data?.data || []);
        } catch {
            setExistingOwnerOptions([]);
        }
    };

    const resetUserForm = () => {
        setSelectedExistingOwner(null);
        setManualForm(EMPTY_MANUAL_FORM);
        setEditingUser(null);
    };

    const openManageUsers = (row) => {
        setActiveClinic(row);
        setManageOpen(true);
        setUserMode('existing');
        resetUserForm();
        fetchClinicUsers(row.eclinicCode);
        fetchExistingOwners(row.eclinicCode);
    };

    const closeManageUsers = () => {
        if (userSaving) return;
        setManageOpen(false);
        setActiveClinic(null);
        setClinicUsers([]);
        setExistingOwnerOptions([]);
        resetUserForm();
    };

    const handleModeChange = (_, mode) => {
        if (!mode) return;
        setUserMode(mode);
        resetUserForm();
    };

    const handleEditUser = (u) => {
        setEditingUser(u);
        setUserMode('manual');
        setManualForm({
            fullName: u.full_name || u.fullName || '',
            phoneNumber: u.phone_number || u.phoneNumber || '',
            email: u.email || '',
        });
    };

    const handleSaveUser = async () => {
        if (!activeClinic) return;

        if (editingUser) {
            if (!manualForm.fullName) {
                toast.error('Please enter full name.');
                return;
            }
            setUserSaving(true);
            try {
                const payload = {
                    fullName: manualForm.fullName,
                    ...(manualForm.email && { email: manualForm.email }),
                    ...(manualForm.phoneNumber && { phoneNumber: normalisePhone(manualForm.phoneNumber) }),
                    roleId: 5,
                };
                const res = await apiService.updateMobilabUser(editingUser.id, payload);
                if (res?.data?.responseCode === '200' || res?.status === 200 || res?.data?.status === true || res?.data?.message) {
                    toast.success('User updated successfully.');
                    resetUserForm();
                    fetchClinicUsers(activeClinic.eclinicCode);
                } else {
                    toast.error(res?.data?.reponseMessage || res?.data?.message || 'Update failed.');
                }
            } catch {
                toast.error('Something went wrong.');
            }
            setUserSaving(false);
            return;
        }

        if (!isEclinic || userMode === 'existing') {
            if (!selectedExistingOwner) {
                toast.error('Please select an user.');
                return;
            }
            setUserSaving(true);
            try {
                const payload = {
                    clinicIds: [activeClinic.eclinicCode],
                    fullName: selectedExistingOwner.userName || selectedExistingOwner.fullName || '',
                    ...(selectedExistingOwner.email && { email: selectedExistingOwner.email }),
                    ...(selectedExistingOwner.mobile && { phoneNumber: normalisePhone(selectedExistingOwner.mobile) }),
                    roleId:  5,
                    createdBy: userCode,
                };
                const res = await apiService.createMobilabUser(payload);
                if (res?.data?.responseCode === '200' || res?.status === 200 || res?.data?.status === true) {
                    toast.success('Owner mapped successfully.');
                    resetUserForm();
                    fetchClinicUsers(activeClinic.eclinicCode);
                    getTableList(currentPage);
                } else {
                    toast.error(res?.data?.mobilabMessage || res?.data?.message || 'Save failed.');
                }
            } catch {
                toast.error('Something went wrong.');
            }
            setUserSaving(false);
            return;
        }

        if (!manualForm.fullName) {
            toast.error('Please enter full name.');
            return;
        }
        setUserSaving(true);
        try {
            const payload = {
                clinicIds: [activeClinic.eclinicCode],
                fullName: manualForm.fullName,
                ...(manualForm.email && { email: manualForm.email }),
                ...(manualForm.phoneNumber && { phoneNumber: normalisePhone(manualForm.phoneNumber) }),
                roleId: 5,
                createdBy: userCode,
            };
            const res = await apiService.createMobilabUser(payload);
            if (res?.data?.responseCode === '200' || res?.status === 200 || res?.data?.status === true) {
                toast.success('User added successfully.');
                resetUserForm();
                fetchClinicUsers(activeClinic.eclinicCode);
                getTableList(currentPage);
            } else {
                toast.error(res?.data?.mobilabMessage || res?.data?.message || 'Save failed.');
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setUserSaving(false);
    };

    const openDeleteUserDialog = (u) => {
        if (u.is_active === false) return;
        setUserToDelete(u);
        setDeleteUserDialogOpen(true);
    };

    const closeDeleteUserDialog = () => {
        setDeleteUserDialogOpen(false);
        setUserToDelete(null);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete || !activeClinic) return;
        setUserSaving(true);
        try {
            const res = await apiService.deleteMobilabUser(userToDelete.id);
            const resData = res?.data;
            const isSuccess =
                res?.status === 200 ||
                resData?.responseCode === '200' ||
                resData?.status === true ||
                resData?.message?.toLowerCase().includes('success') ||
                resData?.reponseMessage?.toLowerCase().includes('success') ||
                resData?.data?.is_active === false;

            if (isSuccess) {
                toast.success('User deleted successfully.');
                closeDeleteUserDialog();
                fetchClinicUsers(activeClinic.eclinicCode);
            } else {
                toast.error(resData?.reponseMessage || resData?.message || 'Delete failed.');
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setUserSaving(false);
    };

    const openDeleteClinicDialog = (row) => {
        setClinicToDelete(row);
        setDeleteClinicDialogOpen(true);
    };

    const closeDeleteClinicDialog = () => {
        if (clinicDeleting) return;
        setDeleteClinicDialogOpen(false);
        setClinicToDelete(null);
    };

    const handleDeleteClinic = async () => {
        if (!clinicToDelete) return;
        setClinicDeleting(true);
        try {
            const res = await apiService.deleteMobilabClinic(clinicToDelete.mobilabClinicId);
            const resData = res?.data;
            const isSuccess =
                res?.status === 200 ||
                resData?.responseCode === '200' ||
                resData?.status === true ||
                resData?.message?.toLowerCase().includes('success') ||
                resData?.reponseMessage?.toLowerCase().includes('success');

            if (isSuccess) {
                toast.success(`${typeLabel} deleted successfully.`);
                closeDeleteClinicDialog();
                setSelectedIndex(null);
                getTableList(currentPage);
            } else {
                toast.error(resData?.reponseMessage || resData?.message || 'Delete failed.');
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setClinicDeleting(false);
    };

    // --- Activate handlers (inactive -> active) ---
    const openActivateDialog = (row) => {
        setClinicToActivate(row);
        setActivateDialogOpen(true);
    };

    const closeActivateDialog = () => {
        if (activating) return;
        setActivateDialogOpen(false);
        setClinicToActivate(null);
    };

    const handleActivateClinic = async () => {
        if (!clinicToActivate) return;
        setActivating(true);
        try {
            const res = await apiService.activateMobilabClinic(clinicToActivate.mobilabClinicId);
            const resData = res?.data;
            const isSuccess =
                res?.status === 200 ||
                resData?.responseCode === '200' ||
                resData?.status === true ||
                resData?.message?.toLowerCase?.().includes('success') ||
                resData?.reponseMessage?.toLowerCase?.().includes('success');

            if (isSuccess) {
                toast.success(`${typeLabel} activated successfully.`);
                setTableList((prev) =>
                    prev.map((row) =>
                        row.mobilabClinicId === clinicToActivate.mobilabClinicId
                            ? { ...row, isActive: 1 }
                            : row
                    )
                );
                closeActivateDialog();
            } else {
                toast.error(resData?.reponseMessage || resData?.message || 'Activation failed.');
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setActivating(false);
    };

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i);
    const visiblePages = pageNumbers.filter(p => p === 0 || p === totalPages - 1 || Math.abs(p - currentPage) <= 1);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 0, px: 0 }}>
            <Paper elevation={0} sx={{ p: 1.25, mb: 1, borderRadius: '10px', border: `1px solid ${T.border}`, background: T.sectionBg, position: 'sticky', top: 64, zIndex: 10 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '8px',
                            background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: `0 2px 8px ${T.primaryMid}`, flexShrink: 0,
                        }}>
                            <LocalHospitalOutlinedIcon sx={{ color: '#fff', fontSize: 17 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.textPrimary, lineHeight: 1.15, margin: 0 }}>
                                Mobilab Clinic List
                            </Typography>
                            <Typography variant="caption" sx={{ color: T.textSecondary, fontSize: '11px', display: 'block', margin: 0 }}>
                                Manage {typeLabel.toLowerCase()}s and their user mapping
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Autocomplete
                            options={filterEclinicOptions}
                            getOptionLabel={(o) => getOptionLabel(o) ? `${getOptionLabel(o)} (${getOptionCode(o)})` : ''}
                            value={filterEclinicOptions.find(e => getOptionCode(e) === filterEclinicCode) || null}
                            onInputChange={(_, v) => getEclinicsData(v)}
                            onChange={(_, v) => handleFilterEclinicChange(v)}
                            isOptionEqualToValue={(o, v) => getOptionCode(o) === getOptionCode(v)}
                            noOptionsText={`Search ${typeLabel.toLowerCase()}`}
                            size="small"
                            sx={{ width: 210 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={`Filter by ${typeLabel}`}
                                    variant="outlined"
                                    placeholder={`Search ${typeLabel.toLowerCase()}`}
                                    sx={{
                                        ...fieldSx,
                                        '& .MuiOutlinedInput-root': {
                                            ...fieldSx['& .MuiOutlinedInput-root'],
                                            minHeight: 34, maxHeight: 34, py: '0px !important'
                                        }
                                    }}
                                />
                            )}
                        />
                        <Autocomplete
                            options={filterUserOptions}
                            loading={filterUserLoading}
                            getOptionLabel={(o) => o?.userName ? `${o.userName}${o.mobile ? ` (${o.mobile})` : ''}` : ''}
                            value={filterUserOptions.find(u => u.userCode === filterUserCode) || null}
                            onOpen={handleFilterUserOpen}
                            onChange={(_, v) => handleFilterUserChange(v)}
                            isOptionEqualToValue={(o, v) => o?.userCode === v?.userCode}
                            noOptionsText="No users found"
                            size="small"
                            sx={{ width: 210 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filter by user"
                                    variant="outlined"
                                    placeholder="Select user"
                                    sx={{
                                        ...fieldSx,
                                        '& .MuiOutlinedInput-root': {
                                            ...fieldSx['& .MuiOutlinedInput-root'],
                                            minHeight: 34, maxHeight: 34, py: '0px !important'
                                        }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {filterUserLoading ? <CircularProgress size={14} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        <Tooltip title="Search" arrow>
                            <IconButton size="small" onClick={handleFilterSearch}
                                sx={{ bgcolor: T.primary, color: '#fff', width: 34, height: 34, borderRadius: '7px', '&:hover': { bgcolor: T.primaryDark } }}>
                                <SearchIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset" arrow>
                            <IconButton size="small" onClick={handleFilterReset}
                                sx={{ border: `1px solid ${T.border}`, color: T.textSecondary, width: 34, height: 34, borderRadius: '7px', '&:hover': { borderColor: T.primary, color: T.primary, bgcolor: T.primaryLight } }}>
                                <RestartAltIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Button
                            variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={openAddDialog}
                            sx={{
                                borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', px: 1.75, height: 34,
                                background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                                boxShadow: `0 2px 8px ${T.primaryMid}`,
                                '&:hover': { background: `linear-gradient(135deg, ${T.primaryDark} 0%, #0d47a1 100%)` },
                            }}
                        >
                            Add {typeLabel}
                        </Button>
                    </Box>
                </Box>

                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                        minHeight: 30,
                        '& .MuiTabs-indicator': { bgcolor: T.primary, height: 2.5, borderRadius: '2px' },
                        '& .MuiTab-root': { minHeight: 30, py: 0.5, fontSize: '0.8125rem' },
                    }}
                >
                    {CLINIC_TABS.map(t => (
                        <Tab
                            key={t.value}
                            value={t.value}
                            label={t.label}
                            icon={t.value === 'ECLINIC' ? <LocalHospitalOutlinedIcon sx={{ fontSize: 16 }} /> : <PlaceOutlinedIcon sx={{ fontSize: 16 }} />}
                            iconPosition="start"
                            sx={{
                                minHeight: 34, textTransform: 'none', fontWeight: 600, fontSize: '13px',
                                color: T.textSecondary, '&.Mui-selected': { color: T.primary },
                            }}
                        />
                    ))}
                </Tabs>
            </Paper>

            {loading && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress sx={{ color: T.primary }} />
                </Box>
            )}

            <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', minHeight: 350, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ background: `linear-gradient(90deg, ${T.primaryLight} 0%, #f8f9ff 100%)` }}>
                                {[`${typeLabel} Code`, `${typeLabel} Name`, 'City', 'Status', 'Actions'].map(col => (
                                    <TableCell key={col} sx={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: T.primary, borderBottom: `2px solid ${T.primaryMid}`, py: 1.25, whiteSpace: 'nowrap', bgcolor: T.primaryLight }}>
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: T.textSecondary }}>
                                            <LocalHospitalOutlinedIcon sx={{ fontSize: 40, opacity: 0.25 }} />
                                            <Typography variant="body2">No {typeLabel.toLowerCase()}s found</Typography>
                                            <Typography variant="caption">Try adjusting your filter or add a new {typeLabel.toLowerCase()}</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableList.map((row, index) => {
                                    const isRowActive = row.isActive === 1;
                                    return (
                                        <TableRow
                                            key={row.id}
                                            onClick={() => setSelectedIndex(index)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: selectedIndex === index ? T.rowSelected : (index % 2 === 0 ? '#fff' : T.rowAlt),
                                                borderLeft: selectedIndex === index ? `3px solid ${T.primary}` : '3px solid transparent',
                                                transition: 'all 0.15s ease',
                                                '&:hover': { bgcolor: T.rowHover, borderLeft: `3px solid ${T.primaryMid}` },
                                                '& td': { borderBottom: `1px solid ${T.border}`, py: 1.1, fontSize: '13px' },
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: T.primary, flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, fontFamily: 'monospace', color: T.primary }}>
                                                        {row.eclinicCode}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 500, color: T.textPrimary }}>{row.eclinicName}</TableCell>
                                            <TableCell>{row.city || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={isRowActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    icon={isRowActive
                                                        ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: `${T.success} !important` }} />
                                                        : <HighlightOffIcon sx={{ fontSize: 14, color: `${T.error} !important` }} />}
                                                    sx={{
                                                        fontSize: '11px', fontWeight: 700, height: 22, borderRadius: '6px',
                                                        bgcolor: isRowActive ? T.successBg : T.errorBg,
                                                        color: isRowActive ? T.success : T.error,
                                                        border: `1px solid ${isRowActive ? T.successMid : T.errorMid}`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                    <Tooltip title="Manage users" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); openManageUsers(row); }}
                                                            sx={{ color: T.primary, '&:hover': { bgcolor: T.primaryLight } }}
                                                        >
                                                            <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {isRowActive ? (
                                                        <Tooltip title={`Delete ${typeLabel.toLowerCase()}`} arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => { e.stopPropagation(); openDeleteClinicDialog(row); }}
                                                                sx={{ color: T.error, '&:hover': { bgcolor: T.errorBg } }}
                                                            >
                                                                <DeleteIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={`Activate ${typeLabel.toLowerCase()}`} arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => { e.stopPropagation(); openActivateDialog(row); }}
                                                                sx={{ color: T.success, '&:hover': { bgcolor: T.successBg } }}
                                                            >
                                                                <PowerSettingsNewIcon sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 1.5, borderTop: `1px solid ${T.border}`, bgcolor: T.sectionBg,
                    flexWrap: 'wrap', gap: 1,
                }}>
                    <Typography variant="caption" sx={{ color: T.textSecondary }}>
                        Showing <strong>{tableList.length}</strong> of <strong>{totalElements}</strong> records
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <button onClick={() => getTableList(currentPage - 1)} disabled={currentPage === 0} style={{ padding: '4px 10px', border: `1px solid ${T.border}`, borderRadius: '7px', background: '#fff', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, fontSize: '14px', fontWeight: 600, color: T.textPrimary }}>
                            ‹
                        </button>
                        {visiblePages.map((p, idx) => {
                            const prev = visiblePages[idx - 1];
                            const ellipsis = prev !== undefined && p - prev > 1;
                            return (
                                <React.Fragment key={p}>
                                    {ellipsis && <span style={{ color: T.textSecondary, padding: '0 2px' }}>…</span>}
                                    <button
                                        onClick={() => getTableList(p)}
                                        style={{
                                            padding: '4px 10px', border: `1px solid ${currentPage === p ? T.primary : T.border}`,
                                            borderRadius: '7px', fontSize: '13px', fontWeight: currentPage === p ? 700 : 400,
                                            background: currentPage === p ? T.primary : '#fff',
                                            color: currentPage === p ? '#fff' : T.textPrimary,
                                            cursor: 'pointer', transition: 'all 0.15s',
                                        }}
                                    >
                                        {p + 1}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                        <button onClick={() => getTableList(currentPage + 1)} disabled={currentPage >= totalPages - 1} style={{ padding: '4px 10px', border: `1px solid ${T.border}`, borderRadius: '7px', background: '#fff', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, fontSize: '14px', fontWeight: 600, color: T.textPrimary }}>
                            ›
                        </button>
                    </Box>
                </Box>
            </Paper>

            <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '14px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, background: `linear-gradient(90deg, ${T.primary} 0%, ${T.primaryDark} 100%)` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <LocalHospitalOutlinedIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 20 }} />
                        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>Add {typeLabel}</Typography>
                    </Box>
                    <IconButton onClick={closeAddDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Typography variant="caption" sx={{ color: T.textSecondary, display: 'block', mb: 1.5 }}>
                        {isEclinic
                            ? `Search and select an existing ${typeLabel.toLowerCase()}, or add one manually. Owners can be mapped afterwards from the ${typeLabel.toLowerCase()}'s "Manage users" action.`
                            : `Search and select a ${typeLabel.toLowerCase()} to onboard it here. Owners can be mapped afterwards from the "Manage users" action.`}
                    </Typography>

                    {isEclinic && (
                        <ToggleButtonGroup value={addMode} exclusive size="small" onChange={handleAddModeChange} sx={{ mb: 2 }}>
                            <ToggleButton value="search" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5, py: 0.25 }}>
                                Search Existing
                            </ToggleButton>
                            <ToggleButton value="manual" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5, py: 0.25 }}>
                                Add Manually
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {isEclinic && addMode === 'manual' ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label={`${typeLabel} Code *`} variant="outlined" size="small"
                                    value={manualEclinicForm.eclinicCode}
                                    onChange={(e) => setManualEclinicForm(p => ({ ...p, eclinicCode: e.target.value }))}
                                    autoFocus
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label={`${typeLabel} Name *`} variant="outlined" size="small"
                                    value={manualEclinicForm.eclinicName}
                                    onChange={(e) => setManualEclinicForm(p => ({ ...p, eclinicName: e.target.value }))}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="City" variant="outlined" size="small"
                                    value={manualEclinicForm.city}
                                    onChange={(e) => setManualEclinicForm(p => ({ ...p, city: e.target.value }))}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="State" variant="outlined" size="small"
                                    value={manualEclinicForm.state}
                                    onChange={(e) => setManualEclinicForm(p => ({ ...p, state: e.target.value }))}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Country" variant="outlined" size="small"
                                    value={manualEclinicForm.country}
                                    onChange={(e) => setManualEclinicForm(p => ({ ...p, country: e.target.value }))}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Zipcode" variant="outlined" size="small"
                                    value={manualEclinicForm.zipcode}
                                    onChange={(e) => setManualEclinicForm(p => ({ ...p, zipcode: e.target.value }))}
                                    sx={fieldSx}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Autocomplete
                            options={addEclinicOptions}
                            getOptionLabel={(o) => getOptionLabel(o)}
                            value={selectedAddEclinic}
                            onInputChange={(_, v) => fetchAddEclinicOptions(v)}
                            onChange={(_, v) => setSelectedAddEclinic(v)}
                            isOptionEqualToValue={(o, v) => getOptionCode(o) === getOptionCode(v)}
                            renderInput={(params) => (
                                <TextField {...params} label={`${typeLabel} *`} variant="outlined" size="small" placeholder={`Search ${typeLabel.toLowerCase()}`} autoFocus sx={fieldSx} />
                            )}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeAddDialog} disabled={addSaving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, color: T.textSecondary, border: `1px solid ${T.border}`, '&:hover': { bgcolor: T.sectionBg } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddClinic} variant="contained" disabled={addSaving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.5, minWidth: 90, bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDark } }}>
                        {addSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `Add ${typeLabel}`}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={manageOpen} onClose={closeManageUsers} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '14px' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, background: `linear-gradient(90deg, ${T.primary} 0%, ${T.primaryDark} 100%)` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <GroupsOutlinedIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 20, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>Manage Users</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.8)' }} noWrap>
                                {activeClinic?.eclinicName} ({activeClinic?.eclinicCode})
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={closeManageUsers} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: T.textSecondary }}>
                        Mapped Users
                    </Typography>
                    <Paper elevation={0} sx={{ mt: 1, mb: 2.5, borderRadius: '10px', border: `1px solid ${T.border}`, maxHeight: 220, overflowY: 'auto' }}>
                        {clinicUsersLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={22} sx={{ color: T.primary }} />
                            </Box>
                        ) : clinicUsers.length === 0 ? (
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: T.textSecondary }}>
                                    No users mapped to this {typeLabel.toLowerCase()} yet.
                                </Typography>
                            </Box>
                        ) : (
                            clinicUsers.map((u, i) => {
                                const isActive = u.is_active !== false;
                                return (
                                    <Box key={u.id}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            px: 1.5, py: 1, borderBottom: i < clinicUsers.length - 1 ? `1px solid ${T.border}` : 'none',
                                            bgcolor: editingUser?.id === u.id ? T.rowSelected : 'transparent',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                            <OwnerAvatar name={u.full_name || u.fullName} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }} noWrap>
                                                    {u.full_name || u.fullName || '-'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11px', color: T.textSecondary }} noWrap>
                                                    {(u.phone_number || u.phoneNumber || '-')}{u.email ? ` · ${u.email}` : ''}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                            <Chip
                                                label={isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                sx={{
                                                    fontSize: '10px', fontWeight: 700, height: 20, borderRadius: '6px', mr: 0.5,
                                                    bgcolor: isActive ? T.successBg : T.errorBg,
                                                    color: isActive ? T.success : T.error,
                                                }}
                                            />
                                            <Tooltip title="Edit" arrow>
                                                <IconButton size="small" onClick={() => handleEditUser(u)} sx={{ color: T.primary }}>
                                                    <EditIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={isActive ? 'Delete' : 'Already inactive'} arrow>
                                                <span>
                                                    <IconButton size="small" disabled={!isActive} onClick={() => openDeleteUserDialog(u)}
                                                        sx={{ color: isActive ? T.error : T.disabled }}>
                                                        <DeleteIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Paper>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: T.textSecondary }}>
                            {editingUser ? 'Edit User' : 'Add User'}
                        </Typography>
                        {!editingUser && isEclinic && (
                            <ToggleButtonGroup value={userMode} exclusive size="small" onChange={handleModeChange}>
                                <ToggleButton value="existing" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5, py: 0.25 }}>
                                    Existing User
                                </ToggleButton>
                                <ToggleButton value="manual" sx={{ textTransform: 'none', fontSize: '12px', px: 1.5, py: 0.25 }}>
                                    Add Manually
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Box>

                    {!editingUser && (!isEclinic || userMode === 'existing') ? (
                        <Autocomplete
                            options={existingOwnerOptions}
                            getOptionLabel={(o) => o?.userName ? `${o.userName}${o.mobile ? ` (${o.mobile})` : ''}` : ''}
                            value={selectedExistingOwner}
                            onChange={(_, v) => setSelectedExistingOwner(v)}
                            isOptionEqualToValue={(o, v) => o?.userCode === v?.userCode}
                            renderInput={(params) => (
                                <TextField {...params} label="Select user *" variant="outlined" size="small" placeholder="Search user" sx={fieldSx}
                                    InputProps={{ ...params.InputProps, startAdornment: (<><InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 16, color: T.textSecondary }} /></InputAdornment>{params.InputProps.startAdornment}</>) }} />
                            )}
                        />
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Full Name *" variant="outlined" size="small"
                                    value={manualForm.fullName}
                                    onChange={(e) => setManualForm(p => ({ ...p, fullName: e.target.value }))}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 16, color: T.textSecondary }} /></InputAdornment> }}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Phone Number" variant="outlined" size="small"
                                    value={manualForm.phoneNumber}
                                    onChange={(e) => setManualForm(p => ({ ...p, phoneNumber: e.target.value }))}
                                    placeholder="e.g. 9876543210"
                                    helperText="Country code 91 added automatically"
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth label="Email" variant="outlined" size="small"
                                    value={manualForm.email}
                                    onChange={(e) => setManualForm(p => ({ ...p, email: e.target.value }))}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 16, color: T.textSecondary }} /></InputAdornment> }}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: T.textSecondary }}>
                                    {typeLabel}: <strong>{activeClinic?.eclinicCode}</strong> (applied automatically)
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    {editingUser && (
                        <Button onClick={resetUserForm} disabled={userSaving}
                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2, color: T.textSecondary, mr: 'auto' }}>
                            Cancel Edit
                        </Button>
                    )}
                    <Button onClick={closeManageUsers} disabled={userSaving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, color: T.textSecondary, border: `1px solid ${T.border}`, '&:hover': { bgcolor: T.sectionBg } }}>
                        Close
                    </Button>
                    <Button onClick={handleSaveUser} variant="contained" disabled={userSaving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.5, minWidth: 100, bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDark } }}>
                        {userSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : (editingUser ? 'Save Changes' : 'Add User')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteUserDialogOpen} onClose={closeDeleteUserDialog}
                PaperProps={{ sx: { borderRadius: '14px', p: 0.5, maxWidth: 420 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5, pb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <WarningAmberRoundedIcon sx={{ color: T.error, fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Delete User</Typography>
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: T.textSecondary }}>
                        Are you sure you want to delete{' '}
                        <strong style={{ color: T.textPrimary }}>{userToDelete?.full_name || userToDelete?.fullName}</strong>?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeDeleteUserDialog}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, color: T.textSecondary, border: `1px solid ${T.border}`, '&:hover': { bgcolor: T.sectionBg } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteUser} variant="contained" disabled={userSaving}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.5, bgcolor: T.error, '&:hover': { bgcolor: '#b71c1c' } }}>
                        {userSaving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteClinicDialogOpen} onClose={closeDeleteClinicDialog}
                PaperProps={{ sx: { borderRadius: '14px', p: 0.5, maxWidth: 420 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5, pb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <WarningAmberRoundedIcon sx={{ color: T.error, fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Delete {typeLabel}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: T.textSecondary }}>
                        Are you sure you want to delete{' '}
                        <strong style={{ color: T.textPrimary }}>{clinicToDelete?.eclinicName}</strong>{' '}
                        ({clinicToDelete?.eclinicCode})? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeDeleteClinicDialog} disabled={clinicDeleting}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, color: T.textSecondary, border: `1px solid ${T.border}`, '&:hover': { bgcolor: T.sectionBg } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteClinic} variant="contained" disabled={clinicDeleting}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.5, bgcolor: T.error, '&:hover': { bgcolor: '#b71c1c' } }}>
                        {clinicDeleting ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Activate confirmation dialog (only for inactive rows) */}
            <Dialog
                open={activateDialogOpen} onClose={closeActivateDialog}
                PaperProps={{ sx: { borderRadius: '14px', p: 0.5, maxWidth: 420 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5, pb: 1 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '10px', bgcolor: T.successBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <PowerSettingsNewIcon sx={{ color: T.success, fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Activate {typeLabel}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: T.textSecondary }}>
                        Are you sure you want to activate{' '}
                        <strong style={{ color: T.textPrimary }}>{clinicToActivate?.eclinicName}</strong>{' '}
                        ({clinicToActivate?.eclinicCode})? It will become available for bookings and user mapping again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeActivateDialog} disabled={activating}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, color: T.textSecondary, border: `1px solid ${T.border}`, '&:hover': { bgcolor: T.sectionBg } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleActivateClinic} variant="contained" disabled={activating}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.5, minWidth: 100, bgcolor: T.success, '&:hover': { bgcolor: '#1b5e20' } }}>
                        {activating ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : 'Activate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MobilabClinicList;